<template>
  <div class="body2 mr-1">{{ $t("manageWorkspaces.workspaceTypes.channel") }}:</div>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom">
    <template v-slot:activator="{ props }">
      <div v-bind="props" role="button" class="d-flex align-center">
        <span class="font-weight-bold">
          {{ currentWorkspace.name }}
        </span>
        <v-icon icon="$triangle"></v-icon>
      </div>
    </template>

    <v-card min-width="400" elevation="2" max-height="450">
      <div class="bg-white pa-4 position-sticky top-0 z-index-9999">
        <v-text-field
          density="comfortable"
          rounded
          min-width="400"
          clear-icon="$x"
          :placeholder="$t('general.search')"
          :aria-label="$t('general.search')"
          prepend-inner-icon="$search"
          clearable
          variant="filled"
          bg-color="indigo-faint"
          :hide-details="true"
          @update:model-value="workspaceSearch = $event"
        >
        </v-text-field>
      </div>

      <v-list>
        <v-list-item density="comfortable">
          <template v-slot:title>
            <div class="caption">
              <span v-text="$t('general.workspaces', { n: total }, total)"></span>
            </div>
          </template>
        </v-list-item>
        <v-list-item
          v-for="(workspace, i) in workspaces"
          :id="i.toString()"
          density="comfortable"
          variant="flat"
          height="56"
          :key="workspace.workspaceId"
          :active="workspace.workspaceId === currentWorkspace.workspaceId"
          @click="selectWorkplace(workspace)"
          v-intersect="onIntersect"
        >
          <template v-slot:title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="40" icon="$channelWorkspace" />
              <span v-text="workspace.workspaceName"></span>
            </div>
          </template>
        </v-list-item>
        <div v-if="loading" class="d-flex align-center justify-center">
          <v-progress-circular size="40" indeterminate></v-progress-circular>
        </div>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { WorkspaceAction } from "@/constants/workplaces";
import { computed, ref, watch } from "vue";
import { handleVirtualScrollData, trackGtmEvent, useResetStore } from "@/_helpers/utils.ts";
import { type MspHierarchyItem, useMspStore } from "@/_store/msp/msp.module.ts";
import type { Pagination } from "@/types.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";

export default {
  setup() {
    const mspStore = useMspStore();
    const accountStore = useAccountStore();
    const workspaceSearch = ref("");
    let workspaceTimer: ReturnType<typeof setTimeout>;
    const total = ref(0);
    const workspaces = ref<MspHierarchyItem[]>([]);
    const pagination = ref<Pagination>({
      page: 0,
      pageSize: 25,
    });
    const loading = ref(false);
    const menu = ref(false);
    const workspaceStore = useWorkspaceStore();
    const currentWorkspace = computed(() => ({
      name: workspaceStore.workspace.name,
      workspaceId: workspaceStore.workspace.workspaceId,
    }));

    const selectWorkplace = async (workplace: MspHierarchyItem) => {
      // Resettings all stores except ignore list. Reset should be 1st as order is important
      useResetStore().allExceptIgnoreList();
      // Getting new workspace details and permissions
      await workspaceStore.updateWorkspaceAndPermissions(workplace.workspaceId, true);
      await accountStore.checkMspAccess();
      menu.value = false;
      trackGtmEvent(GtmAction.MSP_WORKSPACE_SELECTOR, GTMParams.WORKSPACE_SELECTED);
    };

    const onIntersect = async (isIntersecting: boolean, entries: IntersectionObserverEntry[]) => {
      const intersectedItemId = Number(entries[0].target.id);
      if (isIntersecting && intersectedItemId === (workspaces.value ?? []).length - 1) {
        // last tab reached in backend, don't do new request
        if (intersectedItemId === total.value - 1) {
          return;
        }
        await loadMoreWorkplaces();
      }
    };

    const loadMoreWorkplaces = async () => {
      pagination.value = {
        page: pagination.value.page + 1,
        pageSize: 25,
      };
      await getItems(workspaceSearch.value);
    };

    const getItems = async (search: string = "") => {
      loading.value = true;
      try {
        const { data } = await mspStore.getHierarchies({
          search,
          pagination: pagination.value,
        });
        workspaces.value = handleVirtualScrollData(workspaces.value, data.items, "workspaceId");
        total.value = data.total;
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const updateSearch = (value: string, skipRequest = false) => {
      workspaceSearch.value = value;

      if (skipRequest) return;

      // cancel pending call
      clearTimeout(workspaceTimer);
      // delay new call 500ms
      workspaceTimer = setTimeout(async () => {
        workspaces.value = [];
        pagination.value = {
          page: 0,
          pageSize: 25,
        };
        await getItems(value);
      }, 500);
    };

    watch(workspaceSearch, (newVal) => {
      updateSearch(newVal);
    });

    watch(menu, async (opened) => {
      if (opened) {
        pagination.value = {
          page: 0,
          pageSize: 25,
        };
        await getItems();
        trackGtmEvent(GtmAction.MSP_WORKSPACE_SELECTOR, GTMParams.SELECTOR_OPENED);
      } else {
        // Clear search and skip request
        updateSearch("", true);
      }
    });

    return {
      menu,
      loading,
      workspaceSearch,
      workplacesActions: WorkspaceAction,
      workspaces,
      total,
      pagination,
      selectWorkplace,
      onIntersect,
      currentWorkspace,
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-field__outline {
    display: none !important;
  }
}
</style>
